/**
 * PF Revamp — catalogue query hook.
 *
 * Fetches the v2 session-bound catalogue
 *   `GET /workspace/{ws}/sessions/{sessionId}/products`
 * with the v2 role mimetype. Since A1.1 / ISSUE-2231 the response is
 * `ProductCatalogGroupedV2DTO` (BE-owned `productGroups[]`) on BOTH the client
 * and admin mimetypes, so this is a HARD SWITCH: `mapGroupedCatalogue` folds the
 * groups into FE shells and `toResolvedCatalogue` resolves them to the UI
 * `ResolvedCatalogue`. There is no flat fallback (the flat envelope survives only
 * on `/external-service/…`, which the FE never calls).
 *
 * Shell copy (name / description / variant-section label) is resolved here from
 * i18n keyed by `productGroupCode` and injected into the transformer — see
 * `resolveGroupPresentation` below. Individual product names (shell / variant /
 * addon) get the same treatment from the `product` namespace, keyed verbatim by
 * `productCode` — see `buildProductNameResolver` below. The mapper + transformer
 * stay i18n-free; both resolvers are built here from `useTranslation`'s `t` and
 * threaded through as plain functions on the transform's `ctx`.
 *
 * There is NO mock fallback: the query stays disabled until a session and a
 * workspace exist, and the catalogue step shows its own session error + retry in
 * the meantime. A fallback used to serve `getMockProductCatalogue` whenever the
 * session was missing, which made a transient BE failure look like a different
 * catalogue — and `ContinueOrderDialog`, comparing a resumed cart against
 * whatever catalogue was cached, then deleted every line as "no longer
 * available" and auto-saved the emptied draft.
 *
 * The `ws` + `sessionId` are read INTERNALLY (store + UserDataContext) rather
 * than passed by each of the ~10 call sites, so every consumer shares ONE cache
 * key and gets the same catalogue — no cache fragmentation, no call-site churn. They flow into the React Query cache key so a new session
 * (e.g. on resume) refetches. The key is built by the exported
 * `pfCatalogueQueryKey` so out-of-hook cache readers cannot drift from it (D3).
 */

import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type {
  CountryCode,
  ProductCatalogGroupedV2DTO,
  ProductCategoryV2,
  ResolvedCatalogue
} from 'models/purchaseFlowRevamp'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { useAuth0 } from 'utils/auth'
import { useUserData } from 'components/contexts/UserDataContext'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { applyQuotaPreview, quotaPreviewForSession } from 'components/pages/PurchaseFlowRevamp/store/quotaPreview'
import { groupOverlayKeys, mapGroupedCatalogue } from './purchaseFlowRevampCatalogue.grouper'
import { toResolvedCatalogue, type GroupPresentation } from './purchaseFlowRevampCatalogue.transform'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

enum Endpoints {
  /** GET — v2 session-bound, group-bucketed product catalogue. */
  SESSION_PRODUCTS = '/workspace/{workspaceId}/sessions/{sessionId}/products',
}

/* -------------------------------------------------------------------------- *
 *  Cache key (single source of truth — D3)
 * -------------------------------------------------------------------------- */

export interface PfCatalogueQueryKeyParams {
  countryCode: CountryCode
  locale: string
  propertyId?: string
  /** Omitted by out-of-hook readers that don't know it (partial key match). */
  ws?: string | null
  /** Omitted by out-of-hook readers that don't know it (partial key match). */
  sessionId?: string | null
}

/**
 * The catalogue cache key. Exported so out-of-hook cache readers build the SAME
 * key as the hook.
 *
 * D3 (fixed here): `ContinueOrderDialog` used to hand-roll
 * `[…, { countryCode, locale, propertyId }]` while the hook wrote
 * `{ countryCode, locale, propertyId, ws, sessionId }` — different hashes, so the
 * stale-draft-item recovery was a guaranteed cache MISS and `clearStaleItems`
 * never fired. Callers that don't know `ws`/`sessionId` (the dialog resumes a
 * draft *before* a session exists) omit them and rely on React Query's PARTIAL
 * key matching via `getQueriesData`, which deep-partial-matches object segments.
 */
export const pfCatalogueQueryKey = ({
  countryCode,
  locale,
  propertyId,
  ws,
  sessionId,
}: PfCatalogueQueryKeyParams) => [
  EntityKeys.PF_REVAMP_CATALOGUE,
  QueryType.GET_ONE,
  // Only the fields the caller knows: a partial object still matches the full
  // key under `getQueriesData` / `invalidateQueries` (exact: false).
  {
    countryCode,
    locale,
    propertyId,
    ...(ws !== undefined ? { ws } : {}),
    ...(sessionId !== undefined ? { sessionId } : {}),
  },
] as const

/**
 * i18n overlay for shell copy, keyed by BE `productGroupCode`. The wire
 * carries no names; group codes are opaque, so an unresolved key falls back to
 * the grouper's humanized code rather than leaking `PRODUCT_GROUP.name` into UI.
 *
 * Key shape: `purchase_flow_revamp:product_groups.<GROUP_CODE>[__<CATEGORY>].*`
 * — the `__<CATEGORY>` variant names ONE split of a group that spans several
 * categories (e.g. `GROUND_PHOTO__FOOD`), and wins over the bare group key.
 *
 * The graceful fallback matters more than key COVERAGE here: `productGroupCode`
 * is a Salesforce formula field on `Name`, so an Ops-side rename of a WorkType
 * silently moves the wire code — no FE deploy involved, and no signal besides
 * a group quietly reverting to its humanized fallback. Do not tighten this to
 * throw/warn on a miss; that would turn an expected SF edit into noise.
 */
export const pfProductGroupI18nKey = (overlayKey: string, field: 'name' | 'description' | 'variant_section_label' | 'note'): string =>
  `purchase_flow_revamp:product_groups.${overlayKey}.${field}`

const buildGroupPresentationResolver = (
  t: (key: string, options?: object) => string
) => (productGroupCode: string, category: ProductCategoryV2): GroupPresentation | undefined => {
  const read = (field: 'name' | 'description' | 'variant_section_label' | 'note'): string | undefined => {
    // The NAME now lives in the general `product2` namespace, which is not
    // bound to the purchase flow — the same string labels a group in an email
    // or the gallery. The category-split override and the description stay
    // here, because a split is a purchase-flow concern and a description is
    // marketing copy for this surface.
    if (field === 'name') {
      const general = t(`product2:productGroups.${productGroupCode}`, { defaultValue: '' })
      if (general) return general
    }
    for (const key of groupOverlayKeys(productGroupCode, category)) {
      const value = t(pfProductGroupI18nKey(key, field), { defaultValue: '' })
      if (value) return value
    }
    return undefined
  }

  return {
    displayName: read('name'),
    description: read('description'),
    variantSectionLabel: read('variant_section_label'),
    note: read('note'),
  }
}

/**
 * i18n resolver for INDIVIDUAL product names (#26/#36) — sibling to
 * `buildGroupPresentationResolver`, built the same way (from the same `t`) but
 * reading the `product` namespace, keyed VERBATIM by the wire `productCode`
 * (opaque — never parsed/split). This is the namespace the backend's flat
 * `/translation/all` catalogue populates at runtime (e.g. `product:p_4269` →
 * "Einheitswertbescheid") and that ~50 existing call sites already read (e.g.
 * `PurchaseFlowRevamp/helpers/instructionCatalogueProducts.ts`). An unresolved
 * `productCode` returns `undefined` — the transformer's `humanizeProductCode`
 * stays the final fallback, this resolver is only ever an upgrade over it.
 */
/**
 * i18n for a catalogue category label (`product2:categories.<slug>`).
 *
 * These were a hardcoded English `Record` in the transformer until 21 Sep, so
 * every client read "On-site capture" whatever their language. The namespace is
 * `product2` rather than the purchase-flow one because a category name is not a
 * purchase-flow string — the same label belongs in an email or the gallery.
 *
 * An unresolved slug returns undefined and the transformer falls back to its
 * English map, so a category Salesforce adds later still renders rather than
 * showing a raw slug.
 */
const buildCategoryNameResolver = (
  t: (key: string, options?: object) => string
) => (categoryId: string): string | undefined => {
  const value = t(`product2:categories.${categoryId}`, { defaultValue: '' })
  return value || undefined
}

const buildProductNameResolver = (
  t: (key: string, options?: object) => string
) => (productCode: string): string | undefined => {
  const key = `product:${productCode}`
  const value = t(key, { defaultValue: '' })
  // An uninitialised i18next echoes the key back — never let that reach the UI.
  return value && value !== key ? value : undefined
}

/**
 * React Query hook — returns a `ResolvedCatalogue`.
 *
 * Real v2 fetch when a session + workspace are resolved (and the mock escape
 * hatch is off); pure-fn mock otherwise. `ws` + `sessionId` are resolved
 * internally from the store / UserDataContext so all consumers stay cache-
 * coherent — they are NOT parameters. Flow code reads it through `usePfCatalogue`.
 */
export function useCatalogue(countryCode: CountryCode, locale: string, propertyId?: string) {
  const api = useAPIV2<Endpoints>()
  const { loading: authLoading } = useAuth0()
    // 'product' declared alongside 'purchase_flow_revamp' per the components
  // rule (never lean on another mount point having pre-loaded a namespace) —
  // `buildProductNameResolver` below reads `product:<productCode>` with the
  // SAME `t`.
  const { t } = useTranslation(['purchase_flow_revamp', 'product'])

  const sessionId = usePfStore((s) => s.draft.sessionId)
  const quotaPreview = usePfStore((s) => s.quotaPreview)

  const selectWithQuota = useCallback(
    (catalogue: ResolvedCatalogue) =>
      applyQuotaPreview(catalogue, quotaPreviewForSession(quotaPreview, sessionId)),
    [quotaPreview, sessionId]
  )

  // Workspace: admin → picked target; client → its property workspace.
  const ws = usePfSessionWorkspaceId()

  return useQuery({
    // propertyId stays in the key (forces a refetch when the user switches
    // properties); ws + sessionId join it so a fresh session refetches.
    queryKey: pfCatalogueQueryKey({ countryCode, locale, propertyId, ws, sessionId }),
    queryFn: async (): Promise<ResolvedCatalogue> => {
      const resolveGroupPresentation = buildGroupPresentationResolver(t)
      const resolveProductName = buildProductNameResolver(t)
      const resolveCategoryName = buildCategoryNameResolver(t)
      const resolveVariantDescription = (kind: string) =>
        t(`purchase_flow_revamp:variant_kind_description.${kind}`, { defaultValue: '' }) || undefined

      const wire = await api.get<ProductCatalogGroupedV2DTO>(
        Endpoints.SESSION_PRODUCTS,
        { workspaceId: ws as string, sessionId: sessionId as string },
        false
      )
      const grouped = mapGroupedCatalogue(wire)
      return toResolvedCatalogue(grouped, { countryCode, locale, resolveGroupPresentation, resolveProductName, resolveCategoryName, resolveVariantDescription })
    },
    // Roles start all-false and resolve asynchronously, and they decide the
    // request's mimetype — firing before they land asks for a model the
    // endpoint does not produce and comes back 406, which the page shows as
    // "we couldn't load the catalogue".
    enabled: Boolean(!authLoading && countryCode && locale && ws && sessionId),
    select: selectWithQuota,
  })
}

/**
 * The flow's catalogue: the selected property's country and locale (falling back to the user's
 * country and the UI language), keyed on the property. Every consumer reads the same cache entry,
 * so placement can never see a catalogue the rest of the flow has not loaded.
 *
 * @example
 * const catalogue = usePfCatalogue().data
 */
export function usePfCatalogue() {
  const { i18n } = useTranslation()
  const { baseUserData } = useUserData()
  const property = usePfStore((s) => s.property)
  const countryCode = (property?.countryCode ?? baseUserData?.country ?? '') as CountryCode
  const locale = property?.locale ?? i18n.language
  return useCatalogue(countryCode, locale, property?.id)
}
